import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Range, editor } from 'monaco-editor';
import { getMainProgramEditorProxy } from '../../utils/mainProgramEditor';
import { Trans, useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DeviceCardProps } from '../DeviceCardProps';
import HelpPopover from '../../../HelpPopover';
import InputControl from '../Controls/InputControl';
import DriveUnitPort from '../Controls/DriveUnitPort';
import DriveUnitPortControl from '../Controls/DriveUnitPortControl';
import MotorTypeControl from '../Controls/MotorTypeControl';
import MotorDirectionControl from '../Controls/MotorDirectionControl';
import SliderControl from '../Controls/SliderControl';
import DropDownControl from '../Controls/DropDownControl';
import UnsupportedCodeStatus from '../Controls/UnsupportedCodeStatus';
import DeviceCardWrapper from '../DeviceCardWrapper';
import { LinearMechanismIcon, RotationalMechanismIcon } from './mechanismIcons';
import { hasParameterAssignment, removeParameterAssignments, updateParameter } from '../DeviceCardBuilder';
import { useControlInitialization } from '../../../../hooks/controls/useControlInitialization';
import { useDriveUnitPortRegistry } from '../../../../hooks/controls/useDriveUnitPortRegistry';
import { electronics, MOTOR_TYPE } from '../../../../utils/constants';
import { MOTOR_DIRECTION, type MotorDirection } from '../../../../utils/motor-direction';
import { isUnsupportedEnumRawValue, parseKnownEnumRawValue } from '../Controls/unsupportedEnumValue';
import { isPlainNumericLiteral } from '../Controls/unsupportedNumericValue';
import { findConstructorCallEndLine, findMatchingDelimiterIndex, offsetAtLineColumn } from '../../../../utils/pythonSourceScanUtils';

interface SupportedFollowerMotorConfig {
  kind: 'supported';
  port: number;
  motorDirection: MotorDirection;
  name: string;
}

interface UnsupportedFollowerMotorConfig {
  kind: 'unsupported';
  rawCode: string;
  name: string;
}

type FollowerMotorConfig = SupportedFollowerMotorConfig | UnsupportedFollowerMotorConfig;
type SensorLimitKind = 'min' | 'max';

interface SupportedSensorOption {
  variableName: string;
  className: string;
  label: string;
  declarationLine: number;
}

interface SensorPositionLimitSelection {
  minSensor: string;
  maxSensor: string;
}

interface SensorLimitMethodCall {
  startLine: number;
  endLine: number;
  sensorName: string | null;
  sensorRange: Range | null;
}

interface FollowerMotorCall {
  startLine: number;
  endLine: number;
  rawCode: string;
}

const MOTOR_PORTS = Array.from({ length: 8 }, (_, i) => i + 1);
const MAX_FOLLOWER_MOTORS = 6;
const MOTOR_BUTTON_HEIGHT = 32;
export const GEAR_RATIO_FIELD_MIN = 1;
export const GEAR_RATIO_FIELD_MAX = 999;
export const GEAR_RATIO_COMPUTED_MIN = 0.01;
const MOTOR_TYPE_HIGH_SPEED = 'MotorType.HIGH_SPEED';
const MOTOR_TYPE_HIGH_TORQUE = 'MotorType.HIGH_TORQUE';
const MOTOR_TYPE_VALUES = [MOTOR_TYPE_HIGH_SPEED, MOTOR_TYPE_HIGH_TORQUE] as const;
const MOTOR_DIRECTION_FORWARD = 'MotorDirection.FORWARD';
const MOTOR_DIRECTION_REVERSE = 'MotorDirection.REVERSE';
const MOTOR_DIRECTION_VALUES = [MOTOR_DIRECTION_FORWARD, MOTOR_DIRECTION_REVERSE] as const;
const SENSOR_LIMIT_NONE = '';
const SENSOR_LIMIT_METHODS: Record<SensorLimitKind, string> = {
  min: 'add_min_sensor_position_limit',
  max: 'add_max_sensor_position_limit',
};
const SENSOR_LIMIT_CLASS_LABELS: Record<string, string> = {
  BeamBreakSensor: 'Beam Break Sensor',
  MagneticProximitySwitch: 'Magnetic Proximity Switch',
};
const GEAR_RATIO_FRACTION_LITERAL = /^\s*[+-]?(?:\d+(?:\.\d*)?|\.\d+)\s*\/\s*[+-]?(?:\d+(?:\.\d*)?|\.\d+)\s*$/;
const BOOLEAN_LITERAL = /^(true|false|1|0)$/i;

const SOFTWARE_POSITION_LIMIT_PARAMETERS = ['min_position_limit', 'max_position_limit'] as const;

const getEditor = (props: DeviceCardProps) => getMainProgramEditorProxy(props.editor ?? window.__bps_editor ?? undefined);
const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const isValidLine = (model: editor.ITextModel, lineOneBased: number): boolean =>
  Number.isInteger(lineOneBased) && lineOneBased >= 1 && lineOneBased <= model.getLineCount();
const isExpectedMechanismConstructorLine = (line: string, mechanismVarName: string, mechanismClassName: string): boolean => {
  if (!mechanismVarName) return false;
  return new RegExp(`^\\s*${escapeRegex(mechanismVarName)}\\s*=\\s*${escapeRegex(mechanismClassName)}\\s*\\(`).test(line);
};
const isCurrentMechanismConstructorLine = (
  model: editor.ITextModel,
  lineOneBased: number,
  mechanismVarName: string,
  mechanismClassName: string
): boolean =>
  isValidLine(model, lineOneBased) && isExpectedMechanismConstructorLine(model.getLineContent(lineOneBased), mechanismVarName, mechanismClassName);
const defaultBoostMotorName = (index: number) => `Boost ${index + 1}`;
const isSupportedFollowerMotor = (motor: FollowerMotorConfig): motor is SupportedFollowerMotorConfig => motor.kind === 'supported';
const nextDefaultBoostMotorName = (motors: FollowerMotorConfig[]) => {
  const usedNames = new Set(motors.map((motor) => motor.name.trim()).filter(Boolean));
  for (let index = 0; index < MAX_FOLLOWER_MOTORS; index++) {
    const candidateName = defaultBoostMotorName(index);
    if (!usedNames.has(candidateName)) return candidateName;
  }
  return defaultBoostMotorName(motors.length);
};
const parseMotorTypeSetting = (value: unknown) => parseKnownEnumRawValue(value, MOTOR_TYPE_VALUES, MOTOR_TYPE_HIGH_TORQUE);
const parseBooleanSetting = (value: unknown) => /^(true|1)$/i.test(String(value ?? '').trim());
const isUnsupportedBooleanRawValue = (rawValue: unknown) => {
  const rawText = String(rawValue ?? '').trim();
  if (!rawText) return false;
  return !BOOLEAN_LITERAL.test(rawText);
};
const isUnsupportedGearRatioRawValue = (rawValue: unknown) => {
  const rawText = String(rawValue ?? '').trim();
  if (!rawText) return false;
  return !isPlainNumericLiteral(rawText) && !GEAR_RATIO_FRACTION_LITERAL.test(rawText);
};
const buildSensorOptionLabel = (variableName: string, className: string) => `${variableName} (${SENSOR_LIMIT_CLASS_LABELS[className] ?? className})`;

const getFollowerMotorCalls = (
  model: editor.ITextModel,
  startLine: number,
  mechanismName: string,
  maxCalls = Number.POSITIVE_INFINITY
): FollowerMotorCall[] => {
  const motorPrefixRegex = new RegExp(`^\\s*${escapeRegex(mechanismName)}\\.add_motor\\(`);
  const leadMotorSettingRegex = new RegExp(`^\\s*${escapeRegex(mechanismName)}\\.motor_group\\.(max_torque|stop_mode)\\s*=`);
  const calls: FollowerMotorCall[] = [];
  let started = false;
  const code = model.getValue();
  const codeLines = code.split(/\r?\n/);

  for (let lineNum = startLine; lineNum <= model.getLineCount() && calls.length < maxCalls; lineNum++) {
    const line = model.getLineContent(lineNum);
    const trimmed = line.trim();
    if (trimmed.length === 0) {
      if (started) break;
      continue;
    }
    if (!started && leadMotorSettingRegex.test(line)) {
      continue;
    }
    if (motorPrefixRegex.test(line)) {
      started = true;
      const openParenIndex = line.indexOf('(', line.indexOf(`${mechanismName}.add_motor`));
      const openOffset = offsetAtLineColumn(codeLines, lineNum - 1, openParenIndex);
      const closeOffset = findMatchingDelimiterIndex(code, openOffset, '(', ')', { ignoreLineComments: true });
      const endLine = closeOffset >= 0 ? code.slice(0, closeOffset).split(/\r?\n/).length : lineNum;
      const rawCode = codeLines
        .slice(lineNum - 1, endLine)
        .join('\n')
        .trim();
      calls.push({ startLine: lineNum, endLine, rawCode });
      lineNum = endLine;
      continue;
    }
    break;
  }

  return calls;
};

const getPositionInText = (startLine: number, startColumn: number, text: string, offset: number) => {
  const prefix = text.slice(0, offset);
  const lineBreaks = prefix.match(/\n/g)?.length ?? 0;
  if (lineBreaks === 0) {
    return { lineNumber: startLine, column: startColumn + prefix.length };
  }
  const lastLineStart = prefix.lastIndexOf('\n') + 1;
  return { lineNumber: startLine + lineBreaks, column: prefix.length - lastLineStart + 1 };
};

const findClosingParenOffset = (text: string, openParenOffset: number): number | null => {
  let depth = 0;
  let quote: string | null = null;
  let escaped = false;

  for (let index = openParenOffset; index < text.length; index++) {
    const ch = text[index];
    if (quote) {
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === quote) {
        quote = null;
      }
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      continue;
    }
    if (ch === '(') {
      depth++;
    } else if (ch === ')') {
      depth--;
      if (depth === 0) return index;
    }
  }

  return null;
};

const findSensorArgumentInArgs = (argsText: string): { sensorName: string; startOffset: number; endOffset: number } | null => {
  const ranges: Array<{ start: number; end: number }> = [];
  let rangeStart = 0;
  let depth = 0;
  let quote: string | null = null;
  let escaped = false;

  for (let index = 0; index <= argsText.length; index++) {
    const ch = argsText[index];
    if (quote) {
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === quote) {
        quote = null;
      }
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      continue;
    }
    if (ch === '(' || ch === '[' || ch === '{') {
      depth++;
      continue;
    }
    if (ch === ')' || ch === ']' || ch === '}') {
      depth = Math.max(0, depth - 1);
      continue;
    }
    if ((ch === ',' && depth === 0) || index === argsText.length) {
      ranges.push({ start: rangeStart, end: index });
      rangeStart = index + 1;
    }
  }

  for (const [index, range] of ranges.entries()) {
    const segment = argsText.slice(range.start, range.end);
    const keywordMatch = segment.match(/^(\s*)sensor\s*=\s*([A-Za-z_][\w]*)/);
    if (keywordMatch?.[2]) {
      const valueOffset = range.start + keywordMatch[0].lastIndexOf(keywordMatch[2]);
      return {
        sensorName: keywordMatch[2],
        startOffset: valueOffset,
        endOffset: valueOffset + keywordMatch[2].length,
      };
    }

    if (index === 0) {
      const positionalMatch = segment.match(/^(\s*)([A-Za-z_][\w]*)(?!\s*=)/);
      if (positionalMatch?.[2]) {
        const valueOffset = range.start + positionalMatch[1].length;
        return {
          sensorName: positionalMatch[2],
          startOffset: valueOffset,
          endOffset: valueOffset + positionalMatch[2].length,
        };
      }
    }
  }

  return null;
};

const buildSensorLimitDeleteRange = (model: editor.ITextModel, call: SensorLimitMethodCall) =>
  call.endLine < model.getLineCount()
    ? new Range(call.startLine, 1, call.endLine + 1, 1)
    : new Range(call.startLine, 1, call.endLine, model.getLineMaxColumn(call.endLine));

const getSensorLimitMethodCalls = (
  model: editor.ITextModel,
  startLine: number,
  mechanismName: string,
  methodName: string,
  indent = '',
  endLineExclusive = model.getLineCount() + 1
): SensorLimitMethodCall[] => {
  const firstLineRegex = new RegExp(`^${escapeRegex(indent)}${escapeRegex(mechanismName)}\\.${methodName}\\s*\\(`);
  const calls: SensorLimitMethodCall[] = [];

  for (let lineNum = startLine; lineNum < endLineExclusive && lineNum <= model.getLineCount(); lineNum++) {
    const firstLine = model.getLineContent(lineNum);
    const match = firstLine.match(firstLineRegex);
    if (!match) continue;

    let callText = firstLine;
    let endLine = lineNum;
    let closeParenOffset = findClosingParenOffset(callText, match[0].length - 1);
    while (closeParenOffset === null && endLine < model.getLineCount()) {
      endLine++;
      callText += `\n${model.getLineContent(endLine)}`;
      closeParenOffset = findClosingParenOffset(callText, match[0].length - 1);
    }
    if (closeParenOffset === null || endLine >= endLineExclusive) continue;

    const argsStartOffset = match[0].length;
    const sensorArg = findSensorArgumentInArgs(callText.slice(argsStartOffset, closeParenOffset));
    let sensorRange: Range | null = null;
    if (sensorArg) {
      const sensorStart = getPositionInText(lineNum, 1, callText, argsStartOffset + sensorArg.startOffset);
      const sensorEnd = getPositionInText(lineNum, 1, callText, argsStartOffset + sensorArg.endOffset);
      sensorRange = new Range(sensorStart.lineNumber, sensorStart.column, sensorEnd.lineNumber, sensorEnd.column);
    }

    calls.push({
      startLine: lineNum,
      endLine,
      sensorName: sensorArg?.sensorName ?? null,
      sensorRange,
    });
  }

  return calls;
};

const getSensorLimitConfigRegionEndLine = (model: editor.ITextModel, startLine: number, mechanismName: string, indent = ''): number => {
  const mechanismConfigRegex = new RegExp(
    `^${escapeRegex(indent)}${escapeRegex(mechanismName)}\\.(?:motor_group\\.|add_motor\\(|enable_sensor_limits\\s*=)`
  );
  const sensorLimitLineRegex = new RegExp(
    `^${escapeRegex(indent)}${escapeRegex(mechanismName)}\\.(?:add_min_sensor_position_limit|add_max_sensor_position_limit)\\s*\\(`
  );
  const hardwareDeclarationRegex = new RegExp(`^${escapeRegex(indent)}[A-Za-z_][\\w]*\\s*=\\s*[A-Z][A-Za-z0-9_]*\\s*\\(`);
  const generatedSectionBreakRegex = /^#\s*(Map input actions here|Start your robot!)/i;

  let lineNum = startLine;
  while (lineNum <= model.getLineCount()) {
    const line = model.getLineContent(lineNum);
    const trimmed = line.trim();

    if (trimmed.length === 0) {
      lineNum++;
      continue;
    }
    if (generatedSectionBreakRegex.test(trimmed)) {
      break;
    }
    if (trimmed.startsWith('#')) {
      lineNum++;
      continue;
    }
    if (mechanismConfigRegex.test(line)) {
      lineNum++;
      continue;
    }
    if (sensorLimitLineRegex.test(line)) {
      const call = (Object.values(SENSOR_LIMIT_METHODS) as string[])
        .flatMap((methodName) => getSensorLimitMethodCalls(model, lineNum, mechanismName, methodName, indent))
        .find((methodCall) => methodCall.startLine === lineNum);
      if (!call) break;
      lineNum = call.endLine + 1;
      continue;
    }
    if (hardwareDeclarationRegex.test(line)) {
      lineNum = findConstructorCallEndLine(model, lineNum) + 1;
      continue;
    }

    break;
  }

  return lineNum;
};

const getSensorLimitMethodCallsInConfigRegion = (
  model: editor.ITextModel,
  startLine: number,
  mechanismName: string,
  methodName: string,
  indent = ''
) =>
  getSensorLimitMethodCalls(
    model,
    startLine,
    mechanismName,
    methodName,
    indent,
    getSensorLimitConfigRegionEndLine(model, startLine, mechanismName, indent)
  );

const getSensorLimitInsertLine = (model: editor.ITextModel, startLine: number, mechanismName: string, afterLine = startLine - 1): number => {
  const configLineRegex = new RegExp(
    `^\\s*${escapeRegex(mechanismName)}\\.(?:motor_group\\.(?:max_torque|stop_mode)\\s*=|add_motor\\(|enable_sensor_limits\\s*=)`
  );
  const sensorLimitLineRegex = new RegExp(
    `^\\s*${escapeRegex(mechanismName)}\\.(?:add_min_sensor_position_limit|add_max_sensor_position_limit)\\s*\\(`
  );
  let lineNum = startLine;
  let insertLine = startLine;
  let foundConfigLine = false;

  while (lineNum <= model.getLineCount()) {
    const line = model.getLineContent(lineNum);
    if (line.trim().length === 0) {
      if (foundConfigLine) break;
      lineNum++;
      continue;
    }
    if (sensorLimitLineRegex.test(line)) {
      const call = (Object.values(SENSOR_LIMIT_METHODS) as string[])
        .flatMap((methodName) => getSensorLimitMethodCalls(model, lineNum, mechanismName, methodName, line.match(/^(\s*)/)?.[1] ?? ''))
        .find((methodCall) => methodCall.startLine === lineNum);
      if (!call) break;
      foundConfigLine = true;
      insertLine = call.endLine + 1;
      lineNum = call.endLine + 1;
      continue;
    }
    if (!configLineRegex.test(line)) {
      break;
    }
    foundConfigLine = true;
    insertLine = lineNum + 1;
    lineNum++;
  }

  return Math.max(foundConfigLine ? insertLine : startLine, afterLine + 1);
};

const getFollowerMotorInsertLine = (model: editor.ITextModel, startLine: number, mechanismName: string): number => {
  const leadMotorSettingRegex = new RegExp(`^\\s*${escapeRegex(mechanismName)}\\.motor_group\\.(max_torque|stop_mode)\\s*=`);
  let lineNum = startLine;
  let insertLine = startLine;
  let foundLeadMotorSetting = false;

  while (lineNum <= model.getLineCount()) {
    const line = model.getLineContent(lineNum);
    if (line.trim().length === 0) {
      lineNum++;
      continue;
    }
    if (!leadMotorSettingRegex.test(line)) {
      break;
    }
    foundLeadMotorSetting = true;
    insertLine = lineNum + 1;
    lineNum++;
  }

  return foundLeadMotorSetting ? insertLine : startLine;
};

const parseFollowerMotorLine = (line: string): FollowerMotorConfig | null => {
  const trimmedLine = line.trim();
  const portValueMatch = line.match(/\bport\s*=\s*([^,)]+)/);
  const portText = portValueMatch?.[1]?.trim();
  const port = portText && /^\d+$/.test(portText) ? Number.parseInt(portText, 10) : null;
  const directionValueMatch = line.match(/\bmotor_direction\s*=\s*([^,)]+)/);
  const directionText = directionValueMatch?.[1]?.trim();
  const nameMatch = line.match(/\bname\s*=\s*("([^"\\]|\\.)*"|'([^'\\]|\\.)*')/);
  const hasUnsupportedNameExpression = /\bname\s*=/.test(line) && !nameMatch;
  const rawName = nameMatch?.[1];
  let parsedName = '';
  if (rawName && rawName.length >= 2) {
    const quote = rawName[0];
    const inner = rawName.slice(1, -1);
    parsedName = inner
      .replace(/\\\\/g, '\\')
      .replace(new RegExp(`\\\\${quote}`, 'g'), quote)
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t');
  }

  const parsedDirection = directionText
    ? parseKnownEnumRawValue(directionText, MOTOR_DIRECTION_VALUES, MOTOR_DIRECTION_FORWARD)
    : MOTOR_DIRECTION_FORWARD;
  const isUnsupportedDirection = directionText ? isUnsupportedEnumRawValue(directionText, MOTOR_DIRECTION_VALUES) : false;
  if (port === null || isUnsupportedDirection || hasUnsupportedNameExpression) {
    return {
      kind: 'unsupported',
      rawCode: trimmedLine,
      name: parsedName || 'Custom Motor',
    };
  }

  return {
    kind: 'supported',
    port,
    motorDirection: parsedDirection === MOTOR_DIRECTION_REVERSE ? MOTOR_DIRECTION.reverse : MOTOR_DIRECTION.forward,
    name: parsedName,
  };
};

const buildFollowerMotorLine = (mechanismName: string, motor: FollowerMotorConfig, indent: string) => {
  if (motor.kind === 'unsupported') {
    return `${indent}${motor.rawCode}`;
  }
  const directionLiteral = motor.motorDirection === MOTOR_DIRECTION.reverse ? 'MotorDirection.REVERSE' : 'MotorDirection.FORWARD';
  const safeName = (motor.name || '').trim() || defaultBoostMotorName(0);
  const escapedName = safeName.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `${indent}${mechanismName}.add_motor(port=${motor.port}, motor_direction=${directionLiteral}, name="${escapedName}")`;
};

const parseSupportedSensorOptions = (model: editor.ITextModel): SupportedSensorOption[] => {
  const options: SupportedSensorOption[] = [];
  const seen = new Set<string>();
  const declarationRegex = /^([A-Za-z_][\w]*)\s*=\s*(BeamBreakSensor|MagneticProximitySwitch|DistanceSensor)\s*\(/;

  for (let lineNum = 1; lineNum <= model.getLineCount(); lineNum++) {
    const match = model.getLineContent(lineNum).match(declarationRegex);
    if (!match?.[1] || !match?.[2]) continue;
    const variableName = match[1];
    const className = match[2];
    if (!SENSOR_LIMIT_CLASS_LABELS[className] || seen.has(variableName)) continue;
    seen.add(variableName);
    options.push({
      variableName,
      className,
      label: buildSensorOptionLabel(variableName, className),
      declarationLine: lineNum,
    });
  }

  return options;
};

const parseSensorPositionLimitSelection = (
  model: editor.ITextModel,
  startLine: number,
  mechanismName: string,
  mechanismIndent = ''
): SensorPositionLimitSelection => {
  const selection: SensorPositionLimitSelection = { minSensor: SENSOR_LIMIT_NONE, maxSensor: SENSOR_LIMIT_NONE };
  (Object.keys(SENSOR_LIMIT_METHODS) as SensorLimitKind[]).forEach((kind) => {
    const methodCalls = getSensorLimitMethodCallsInConfigRegion(model, startLine, mechanismName, SENSOR_LIMIT_METHODS[kind], mechanismIndent);
    for (const call of methodCalls.slice().reverse()) {
      if (call.sensorName) {
        selection[kind === 'min' ? 'minSensor' : 'maxSensor'] = call.sensorName;
        break;
      }
    }
  });
  return selection;
};

interface FollowerMotorPortControlProps {
  id: string;
  labelId: string;
  labelPopoverId: string;
  labelPopover: string;
  selectedPort: number;
  onChange: (portNumber: number) => void;
}

const FollowerMotorPortControl = ({ id, labelId, labelPopoverId, labelPopover, selectedPort, onChange }: FollowerMotorPortControlProps) => (
  <Box>
    <InputLabel sx={{ paddingBottom: 1 }} id={labelId}>
      <Stack direction="row" spacing={1}>
        <Typography>
          <Trans i18nKey="inputControl.motor_port">Motor Port</Trans>
        </Typography>
        <HelpPopover id={labelPopoverId} text={labelPopover} />
      </Stack>
    </InputLabel>
    <Stack direction="column" spacing={1}>
      <Stack direction="row" spacing={1}>
        <DriveUnitPort id={id} portNumber={1} selected={selectedPort} onClick={() => onChange(1)} />
        <DriveUnitPort id={id} portNumber={2} selected={selectedPort} onClick={() => onChange(2)} />
        <DriveUnitPort id={id} portNumber={3} selected={selectedPort} onClick={() => onChange(3)} />
        <DriveUnitPort id={id} portNumber={4} selected={selectedPort} onClick={() => onChange(4)} />
      </Stack>
      <Stack direction="row" spacing={1}>
        <DriveUnitPort id={id} portNumber={5} selected={selectedPort} onClick={() => onChange(5)} />
        <DriveUnitPort id={id} portNumber={6} selected={selectedPort} onClick={() => onChange(6)} />
        <DriveUnitPort id={id} portNumber={7} selected={selectedPort} onClick={() => onChange(7)} />
        <DriveUnitPort id={id} portNumber={8} selected={selectedPort} onClick={() => onChange(8)} />
      </Stack>
    </Stack>
  </Box>
);

const RotationalMechanism = (props: DeviceCardProps) => {
  const { t, i18n } = useTranslation();
  const isLinearMechanism = props.mechanismClass?.name === 'LinearMechanism';
  const HeaderIcon = isLinearMechanism ? LinearMechanismIcon : RotationalMechanismIcon;
  const [motorsExpanded, setMotorsExpanded] = useState(false);
  const [mechanismExpanded, setMechanismExpanded] = useState(false);
  const [activeMotorTab, setActiveMotorTab] = useState(0);
  const [followerMotors, setFollowerMotors] = useState<FollowerMotorConfig[]>([]);
  const [supportedSensorOptions, setSupportedSensorOptions] = useState<SupportedSensorOption[]>([]);
  const [sensorPositionLimitSelection, setSensorPositionLimitSelection] = useState<SensorPositionLimitSelection>({
    minSensor: SENSOR_LIMIT_NONE,
    maxSensor: SENSOR_LIMIT_NONE,
  });

  const mechanismVarName = props.parentName;
  const positionLimitUnitLabel = isLinearMechanism
    ? t('rotationalMechanism.position_limit_unit_meters', { defaultValue: 'meters' })
    : t('rotationalMechanism.position_limit_unit_degrees', { defaultValue: 'degrees' });

  const ratioPreviewFormatter = useMemo(() => {
    const locale = i18n.resolvedLanguage || i18n.language;
    return new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }, [i18n.language, i18n.resolvedLanguage]);

  const motorTypeProps = useMemo(() => ({ ...props, parameterName: 'motor_type' } as DeviceCardProps), [props]);
  const enableSoftwareLimitsProps = useMemo(() => ({ ...props, parameterName: 'enable_software_limits' } as DeviceCardProps), [props]);
  const enableSensorLimitsProps = useMemo(() => ({ ...props, parameterName: 'enable_sensor_limits' } as DeviceCardProps), [props]);
  const minPositionLimitProps = useMemo(() => ({ ...props, parameterName: 'min_position_limit' } as DeviceCardProps), [props]);
  const { value: motorTypeValue } = useControlInitialization<string>(motorTypeProps, {
    parseValue: parseMotorTypeSetting,
    isUnsupportedRawValue: (rawValue) => isUnsupportedEnumRawValue(rawValue, MOTOR_TYPE_VALUES),
  });
  const torqueLimits = electronics[motorTypeValue === MOTOR_TYPE_HIGH_SPEED ? MOTOR_TYPE.HIGH_SPEED : MOTOR_TYPE.HIGH_TORQUE];

  const {
    value: isSoftwarePositionLimitsEnabled,
    setValue: setIsSoftwarePositionLimitsEnabled,
    unsupportedRawValue: unsupportedSoftwareLimitsRawValue,
  } = useControlInitialization<boolean>(enableSoftwareLimitsProps, {
    parseValue: parseBooleanSetting,
    isUnsupportedRawValue: isUnsupportedBooleanRawValue,
  });

  const onToggleSoftwarePositionLimits = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setIsSoftwarePositionLimitsEnabled(checked);
    const ed = getEditor(props);
    ed?.pushUndoStop();
    try {
      const groupedEditOptions = { undoStop: false, focusEditor: false };
      updateParameter(enableSoftwareLimitsProps, checked ? 'True' : 'False', groupedEditOptions);
      if (checked) {
        if (!hasParameterAssignment(minPositionLimitProps)) {
          updateParameter(minPositionLimitProps, '0', groupedEditOptions);
        }
      } else {
        removeParameterAssignments(props, SOFTWARE_POSITION_LIMIT_PARAMETERS, groupedEditOptions);
      }
    } finally {
      ed?.pushUndoStop();
      ed?.focus();
    }
  };

  const {
    value: isSensorPositionLimitsEnabled,
    setValue: setIsSensorPositionLimitsEnabled,
    unsupportedRawValue: unsupportedSensorLimitsRawValue,
  } = useControlInitialization<boolean>(enableSensorLimitsProps, {
    parseValue: parseBooleanSetting,
    isUnsupportedRawValue: isUnsupportedBooleanRawValue,
  });

  const removeSensorPositionLimitCalls = useCallback(() => {
    const ed = getEditor(props);
    const model = ed?.getModel();
    if (!ed || !model || !Number.isFinite(props.lineNumber) || !mechanismVarName) return;

    const constructorStartLine = props.lineNumber + 1;
    if (!isCurrentMechanismConstructorLine(model, constructorStartLine, mechanismVarName, props.mechanismClass.name)) return;
    const constructorEndLine = findConstructorCallEndLine(model, constructorStartLine);
    const mechanismIndent = model.getLineContent(constructorStartLine).match(/^(\s*)/)?.[1] ?? '';
    const methodCalls = (Object.keys(SENSOR_LIMIT_METHODS) as SensorLimitKind[]).flatMap((kind) =>
      getSensorLimitMethodCallsInConfigRegion(model, constructorEndLine + 1, mechanismVarName, SENSOR_LIMIT_METHODS[kind], mechanismIndent)
    );
    const deleteEdits: editor.IIdentifiedSingleEditOperation[] = methodCalls
      .sort((a, b) => b.startLine - a.startLine)
      .map((call) => ({ range: buildSensorLimitDeleteRange(model, call), text: null, forceMoveMarkers: true }));

    if (deleteEdits.length === 0) return;
    ed.pushUndoStop();
    ed.executeEdits('', deleteEdits);
    ed.pushUndoStop();
  }, [mechanismVarName, props]);

  const onToggleSensorPositionLimits = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setIsSensorPositionLimitsEnabled(checked);
    updateParameter(enableSensorLimitsProps, checked ? 'True' : 'False');
    if (!checked) {
      removeSensorPositionLimitCalls();
      setSensorPositionLimitSelection({ minSensor: SENSOR_LIMIT_NONE, maxSensor: SENSOR_LIMIT_NONE });
    }
  };

  const maxPositionLimitProps = useMemo(() => ({ ...props, parameterName: 'max_position_limit' } as DeviceCardProps), [props]);

  const softwarePositionLimitControls = (
    <Stack direction="column" spacing={2}>
      <InputControl
        {...minPositionLimitProps}
        label={<Trans i18nKey="rotationalMechanism.minimum_limit">Minimum Limit</Trans>}
        labelId="min-position-limit"
        step={0.1}
        unitLabel={positionLimitUnitLabel}
      />
      <InputControl
        {...maxPositionLimitProps}
        label={<Trans i18nKey="rotationalMechanism.maximum_limit">Maximum Limit</Trans>}
        labelId="max-position-limit"
        step={0.1}
        unitLabel={positionLimitUnitLabel}
        showEmptyWhenUnset
      />
    </Stack>
  );

  const {
    portControlValue: leadMotorPortControlValue,
    setPortControlValue: setLeadMotorPortControlValue,
    unsupportedRawValue: leadMotorUnsupportedRawValue,
  } = useDriveUnitPortRegistry(props, 'motor_port');

  const gearRatioProps = useMemo(() => ({ ...props, parameterName: 'gear_ratio' } as DeviceCardProps), [props]);

  const parseGearRatio = useCallback((raw: unknown) => {
    if (typeof raw === 'number') return raw;
    if (typeof raw !== 'string') return Number.NaN;
    const trimmed = raw.trim();
    // Support code emitted as a formula like "4 / 1" or "4/1".
    const fractionMatch = trimmed.match(/^\s*([+-]?(?:\d+(?:\.\d*)?|\.\d+))\s*\/\s*([+-]?(?:\d+(?:\.\d*)?|\.\d+))\s*$/);
    if (fractionMatch) {
      const numerator = Number.parseFloat(fractionMatch[1]);
      const denominator = Number.parseFloat(fractionMatch[2]);
      if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0) return Number.NaN;
      return numerator / denominator;
    }

    return Number.parseFloat(trimmed);
  }, []);

  const {
    value: gearRatioValue,
    setValue: setGearRatioValue,
    unsupportedRawValue: unsupportedGearRatioRawValue,
  } = useControlInitialization<number>(gearRatioProps, {
    max: GEAR_RATIO_FIELD_MAX,
    parseValue: parseGearRatio,
    isUnsupportedRawValue: isUnsupportedGearRatioRawValue,
  });

  const [gearRatioInput, setGearRatioInput] = useState('1');
  const [gearRatioOutput, setGearRatioOutput] = useState('1');
  const activeGearRatioEditRef = useRef<null | 'input' | 'output'>(null);
  const skipNextGearRatioDeriveRef = useRef(false);

  // Keep rounding as a numeric operation (not string formatting). `toFixed(2)` returns a string and isn't locale-aware,
  // so we only use it at the serialization edge when writing back into code; the UI preview uses Intl.NumberFormat.
  const roundTo2 = useCallback((value: number) => Math.round(value * 100) / 100, []);

  const deriveGearRatioFields = useCallback(
    (ratio: unknown) => {
      const parsed = typeof ratio === 'number' ? ratio : Number.parseFloat(String(ratio));
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return { input: '1', output: '1' };
      }

      if (parsed >= 1) {
        const clamped = Math.min(parsed, GEAR_RATIO_FIELD_MAX);
        return { input: String(roundTo2(clamped)), output: '1' };
      }

      const inverse = 1 / parsed;
      const clampedOutput = Math.min(inverse, GEAR_RATIO_FIELD_MAX);
      return { input: '1', output: String(roundTo2(clampedOutput)) };
    },
    [roundTo2]
  );

  useEffect(() => {
    if (activeGearRatioEditRef.current) return;
    if (skipNextGearRatioDeriveRef.current) {
      skipNextGearRatioDeriveRef.current = false;
      return;
    }
    const next = deriveGearRatioFields(gearRatioValue);
    setGearRatioInput(next.input);
    setGearRatioOutput(next.output);
  }, [deriveGearRatioFields, gearRatioValue]);

  const parsedGearRatioInput = useMemo(() => Number.parseFloat(gearRatioInput), [gearRatioInput]);
  const parsedGearRatioOutput = useMemo(() => Number.parseFloat(gearRatioOutput), [gearRatioOutput]);

  const gearRatioComputed = useMemo(() => {
    if (!Number.isFinite(parsedGearRatioInput) || !Number.isFinite(parsedGearRatioOutput) || parsedGearRatioOutput === 0) return null;
    return parsedGearRatioInput / parsedGearRatioOutput;
  }, [parsedGearRatioInput, parsedGearRatioOutput]);

  const gearRatioComputedRounded = useMemo(() => {
    if (gearRatioComputed === null) return null;
    return roundTo2(gearRatioComputed);
  }, [gearRatioComputed, roundTo2]);

  const isGearRatioComputedInRange = useMemo(() => {
    // Mechanical constraint: enforce the raw ratio bounds (do not round into validity).
    if (gearRatioComputed === null) return false;
    return gearRatioComputed >= GEAR_RATIO_COMPUTED_MIN && gearRatioComputed <= GEAR_RATIO_FIELD_MAX;
  }, [gearRatioComputed]);

  const isGearRatioInputValid = useMemo(() => {
    if (!Number.isFinite(parsedGearRatioInput)) return false;
    return parsedGearRatioInput >= GEAR_RATIO_FIELD_MIN && parsedGearRatioInput <= GEAR_RATIO_FIELD_MAX;
  }, [parsedGearRatioInput]);

  const isGearRatioOutputValid = useMemo(() => {
    if (!Number.isFinite(parsedGearRatioOutput)) return false;
    if (parsedGearRatioOutput === 0) return false;
    return parsedGearRatioOutput >= GEAR_RATIO_FIELD_MIN && parsedGearRatioOutput <= GEAR_RATIO_FIELD_MAX;
  }, [parsedGearRatioOutput]);

  const gearRatioDecimalPreview = useMemo(() => {
    if (!isGearRatioInputValid || !isGearRatioOutputValid) return null;
    if (!isGearRatioComputedInRange) return null;
    if (gearRatioComputedRounded === null) return null;
    return ratioPreviewFormatter.format(gearRatioComputedRounded);
  }, [gearRatioComputedRounded, isGearRatioComputedInRange, isGearRatioInputValid, isGearRatioOutputValid, ratioPreviewFormatter]);

  const commitGearRatio = useCallback(
    (nextValues?: { input?: string; output?: string }) => {
      const inputValue = nextValues?.input ?? gearRatioInput;
      const outputValue = nextValues?.output ?? gearRatioOutput;
      const normalizedInputValue = inputValue.trim() === '' ? '1' : inputValue;
      const normalizedOutputValue = outputValue.trim() === '' ? '1' : outputValue;

      if (normalizedInputValue !== gearRatioInput) {
        setGearRatioInput(normalizedInputValue);
      }
      if (normalizedOutputValue !== gearRatioOutput) {
        setGearRatioOutput(normalizedOutputValue);
      }

      const parsedInput = Number.parseFloat(normalizedInputValue);
      const parsedOutput = Number.parseFloat(normalizedOutputValue);

      const inputValid = Number.isFinite(parsedInput) && parsedInput >= GEAR_RATIO_FIELD_MIN && parsedInput <= GEAR_RATIO_FIELD_MAX;
      const outputValid =
        Number.isFinite(parsedOutput) && parsedOutput !== 0 && parsedOutput >= GEAR_RATIO_FIELD_MIN && parsedOutput <= GEAR_RATIO_FIELD_MAX;

      if (!inputValid || !outputValid) return;

      const computedRatio = parsedInput / parsedOutput;
      if (!Number.isFinite(computedRatio)) return;

      // Mechanical constraint: enforce the raw ratio bounds (do not round into validity).
      const isComputedInRange = computedRatio >= GEAR_RATIO_COMPUTED_MIN && computedRatio <= GEAR_RATIO_FIELD_MAX;
      if (!isComputedInRange) return;

      // After passing the hard limit, clamp+round for persistence.
      const clampedRatio = Math.max(GEAR_RATIO_COMPUTED_MIN, Math.min(computedRatio, GEAR_RATIO_FIELD_MAX));
      const roundedRatio = roundTo2(clampedRatio);

      // Preserve the user's entered input/output ratio display on commit.
      // We still update the underlying single `gear_ratio` parameter, but avoid immediately "canonicalizing" the fields.
      skipNextGearRatioDeriveRef.current = true;

      // Optionally snap the displayed fields to 2dp without changing their structure (e.g. keep 4.2:2, not 2.1:1).
      setGearRatioInput((prev) => {
        const parsed = Number.parseFloat(prev);
        return Number.isFinite(parsed) ? roundTo2(parsed).toFixed(2).replace(/\.00$/, '') : prev;
      });
      setGearRatioOutput((prev) => {
        const parsed = Number.parseFloat(prev);
        return Number.isFinite(parsed) ? roundTo2(parsed).toFixed(2).replace(/\.00$/, '') : prev;
      });

      setGearRatioValue(roundedRatio);
      const formatTerm = (value: number) =>
        value
          .toFixed(2)
          .replace(/\.00$/, '')
          .replace(/(\.\d)0$/, '$1');
      updateParameter(gearRatioProps, `${formatTerm(roundTo2(parsedInput))} / ${formatTerm(roundTo2(parsedOutput))}`);
    },
    [gearRatioInput, gearRatioOutput, gearRatioProps, roundTo2, setGearRatioValue]
  );

  const parseSensorLimitsFromEditor = useCallback(() => {
    const ed = getEditor(props);
    const model = ed?.getModel();
    if (!model || !mechanismVarName) {
      setSupportedSensorOptions([]);
      setSensorPositionLimitSelection({ minSensor: SENSOR_LIMIT_NONE, maxSensor: SENSOR_LIMIT_NONE });
      return;
    }

    const constructorStartLine = props.lineNumber + 1;
    if (!isCurrentMechanismConstructorLine(model, constructorStartLine, mechanismVarName, props.mechanismClass.name)) {
      setSupportedSensorOptions(parseSupportedSensorOptions(model));
      setSensorPositionLimitSelection({ minSensor: SENSOR_LIMIT_NONE, maxSensor: SENSOR_LIMIT_NONE });
      return;
    }
    const constructorEndLine = findConstructorCallEndLine(model, constructorStartLine);
    const mechanismIndent = model.getLineContent(constructorStartLine).match(/^(\s*)/)?.[1] ?? '';
    setSupportedSensorOptions(parseSupportedSensorOptions(model));
    setSensorPositionLimitSelection(parseSensorPositionLimitSelection(model, constructorEndLine + 1, mechanismVarName, mechanismIndent));
  }, [mechanismVarName, props]);

  const parseFollowersFromEditor = useCallback(() => {
    const ed = getEditor(props);
    const model = ed?.getModel();
    if (!model || !Number.isFinite(props.lineNumber) || !mechanismVarName) {
      setFollowerMotors([]);
      return;
    }

    const constructorStartLine = props.lineNumber + 1;
    if (!isCurrentMechanismConstructorLine(model, constructorStartLine, mechanismVarName, props.mechanismClass.name)) {
      setFollowerMotors([]);
      return;
    }
    const constructorEndLine = findConstructorCallEndLine(model, constructorStartLine);
    const parsed: FollowerMotorConfig[] = [];
    const motorCalls = getFollowerMotorCalls(model, constructorEndLine + 1, mechanismVarName, MAX_FOLLOWER_MOTORS);
    for (const call of motorCalls) {
      const motor = parseFollowerMotorLine(call.rawCode);
      if (motor) {
        if (!motor.name) {
          motor.name = defaultBoostMotorName(parsed.length);
        }
        parsed.push(motor);
      }
    }

    setFollowerMotors(parsed);
  }, [mechanismVarName, props]);

  useEffect(() => {
    const ed = getEditor(props);
    const model = ed?.getModel();
    if (!ed || !model) return;

    parseSensorLimitsFromEditor();
    const disposable = ed.onDidChangeModelContent(() => {
      parseSensorLimitsFromEditor();
    });
    return () => disposable.dispose();
  }, [parseSensorLimitsFromEditor, props]);

  useEffect(() => {
    const ed = getEditor(props);
    const model = ed?.getModel();
    if (!ed || !model) return;

    parseFollowersFromEditor();
    const disposable = ed.onDidChangeModelContent(() => {
      parseFollowersFromEditor();
    });
    return () => disposable.dispose();
  }, [parseFollowersFromEditor, props]);

  useEffect(() => {
    const maxTabIndex = followerMotors.length;
    if (activeMotorTab > maxTabIndex) {
      setActiveMotorTab(maxTabIndex);
    }
  }, [activeMotorTab, followerMotors.length]);

  const applyFollowerMotorEdits = useCallback(
    (nextFollowers: FollowerMotorConfig[]) => {
      const ed = getEditor(props);
      const model = ed?.getModel();
      if (!ed || !model || !Number.isFinite(props.lineNumber)) return;
      const safeFollowers = nextFollowers.slice(0, MAX_FOLLOWER_MOTORS);
      const constructorStartLine = props.lineNumber + 1;
      if (!isCurrentMechanismConstructorLine(model, constructorStartLine, mechanismVarName, props.mechanismClass.name)) return;
      const constructorEndLine = findConstructorCallEndLine(model, constructorStartLine);
      const motorCalls = getFollowerMotorCalls(model, constructorEndLine + 1, mechanismVarName);

      const edits: editor.IIdentifiedSingleEditOperation[] = motorCalls
        .slice()
        .reverse()
        .map((call) => {
          const lineCount = model.getLineCount();
          const range =
            call.endLine < lineCount
              ? new Range(call.startLine, 1, call.endLine + 1, 1)
              : new Range(call.startLine, 1, call.endLine, model.getLineMaxColumn(call.endLine));
          return { range, text: null, forceMoveMarkers: true };
        });

      if (safeFollowers.length > 0) {
        const baseLine = model.getLineContent(constructorStartLine);
        const indent = (baseLine.match(/^(\s*)/)?.[1] ?? '') || '';
        const insertionText =
          safeFollowers
            .map((motor, index) => ({
              ...motor,
              name: isSupportedFollowerMotor(motor) ? (motor.name || '').trim() || defaultBoostMotorName(index) : motor.name,
            }))
            .map((motor) => buildFollowerMotorLine(mechanismVarName, motor, indent))
            .join('\n') + '\n';
        const insertLine = getFollowerMotorInsertLine(model, constructorEndLine + 1, mechanismVarName);

        if (insertLine <= model.getLineCount()) {
          edits.push({
            range: new Range(insertLine, 1, insertLine, 1),
            text: insertionText,
            forceMoveMarkers: true,
          });
        } else {
          const lineCount = model.getLineCount();
          const endCol = model.getLineMaxColumn(lineCount);
          const needsLeadingNewline = model.getLineContent(lineCount).trim().length > 0 ? '\n' : '';
          edits.push({
            range: new Range(lineCount, endCol, lineCount, endCol),
            text: needsLeadingNewline + insertionText,
            forceMoveMarkers: true,
          });
        }
      }

      ed.pushUndoStop();
      ed.executeEdits('', edits);
      ed.pushUndoStop();
      setFollowerMotors(safeFollowers);
    },
    [mechanismVarName, props]
  );

  const applySensorPositionLimitSelection = useCallback(
    (kind: SensorLimitKind, nextSelection: SensorPositionLimitSelection) => {
      const ed = getEditor(props);
      const model = ed?.getModel();
      if (!ed || !model || !Number.isFinite(props.lineNumber) || !mechanismVarName) return;

      const constructorStartLine = props.lineNumber + 1;
      if (!isCurrentMechanismConstructorLine(model, constructorStartLine, mechanismVarName, props.mechanismClass.name)) return;
      const constructorEndLine = findConstructorCallEndLine(model, constructorStartLine);
      const mechanismIndent = model.getLineContent(constructorStartLine).match(/^(\s*)/)?.[1] ?? '';
      const methodCalls = getSensorLimitMethodCallsInConfigRegion(
        model,
        constructorEndLine + 1,
        mechanismVarName,
        SENSOR_LIMIT_METHODS[kind],
        mechanismIndent
      );

      const nextSensor = kind === 'min' ? nextSelection.minSensor : nextSelection.maxSensor;
      const primaryCall = methodCalls[methodCalls.length - 1];
      const deleteEdits: editor.IIdentifiedSingleEditOperation[] =
        nextSensor && primaryCall?.sensorRange
          ? methodCalls
              .slice(0, -1)
              .sort((a, b) => b.startLine - a.startLine)
              .map((call) => ({ range: buildSensorLimitDeleteRange(model, call), text: null, forceMoveMarkers: true }))
          : methodCalls
              .slice()
              .sort((a, b) => b.startLine - a.startLine)
              .map((call) => ({ range: buildSensorLimitDeleteRange(model, call), text: null, forceMoveMarkers: true }));
      const replaceEdit: editor.IIdentifiedSingleEditOperation | null =
        nextSensor && primaryCall?.sensorRange ? { range: primaryCall.sensorRange, text: nextSensor, forceMoveMarkers: true } : null;
      const shouldInsertCall = Boolean(nextSensor && !replaceEdit);

      if (deleteEdits.length === 0 && replaceEdit === null && !shouldInsertCall) return;
      ed.pushUndoStop();
      const replacementEdits = replaceEdit ? [replaceEdit] : [];
      if (deleteEdits.length > 0 || replacementEdits.length > 0) {
        ed.executeEdits('', [...deleteEdits, ...replacementEdits]);
      }

      const updatedModel = ed.getModel();
      if (shouldInsertCall && updatedModel && nextSensor) {
        const constructorEndLine = findConstructorCallEndLine(updatedModel, constructorStartLine);
        const baseLine = updatedModel.getLineContent(constructorStartLine);
        const indent = (baseLine.match(/^(\s*)/)?.[1] ?? '') || '';
        const insertionText = `${indent}${mechanismVarName}.${SENSOR_LIMIT_METHODS[kind]}(${nextSensor})\n`;
        const selectedSensorLine = parseSupportedSensorOptions(updatedModel).find((option) => option.variableName === nextSensor)?.declarationLine;
        const insertLine = getSensorLimitInsertLine(updatedModel, constructorEndLine + 1, mechanismVarName, selectedSensorLine);
        const insertEdits: editor.IIdentifiedSingleEditOperation[] = [];

        if (insertLine <= updatedModel.getLineCount()) {
          insertEdits.push({
            range: new Range(insertLine, 1, insertLine, 1),
            text: insertionText,
            forceMoveMarkers: true,
          });
        } else {
          const endLine = updatedModel.getLineCount();
          const endCol = updatedModel.getLineMaxColumn(endLine);
          const needsLeadingNewline = updatedModel.getLineContent(endLine).trim().length > 0 ? '\n' : '';
          insertEdits.push({
            range: new Range(endLine, endCol, endLine, endCol),
            text: needsLeadingNewline + insertionText,
            forceMoveMarkers: true,
          });
        }
        ed.executeEdits('', insertEdits);
      }

      ed.pushUndoStop();
      setSensorPositionLimitSelection(nextSelection);
    },
    [mechanismVarName, props]
  );

  const onSelectSensorPositionLimit = (kind: SensorLimitKind, value: string) => {
    const supportedValues = new Set(supportedSensorOptions.map((option) => option.variableName));
    const safeValue = value === SENSOR_LIMIT_NONE || supportedValues.has(value) ? value : SENSOR_LIMIT_NONE;
    const nextSelection: SensorPositionLimitSelection =
      kind === 'min' ? { ...sensorPositionLimitSelection, minSensor: safeValue } : { ...sensorPositionLimitSelection, maxSensor: safeValue };
    setSensorPositionLimitSelection(nextSelection);
    applySensorPositionLimitSelection(kind, nextSelection);
  };

  const renderSensorPositionLimitSelect = (kind: SensorLimitKind) => {
    const selectedSensor = kind === 'min' ? sensorPositionLimitSelection.minSensor : sensorPositionLimitSelection.maxSensor;
    const supportedValues = new Set(supportedSensorOptions.map((option) => option.variableName));
    const safeValue = supportedValues.has(selectedSensor) ? selectedSensor : SENSOR_LIMIT_NONE;
    const label =
      kind === 'min'
        ? t('rotationalMechanism.minimum_sensor_limit', { defaultValue: 'Minimum Limit Sensor' })
        : t('rotationalMechanism.maximum_sensor_limit', { defaultValue: 'Maximum Limit Sensor' });
    const selectId = `${kind}-sensor-position-limit`;
    const labelId = `${selectId}-label`;

    return (
      <FormControl variant="standard" sx={{ width: 220 }}>
        <InputLabel id={labelId} shrink>
          {label}
        </InputLabel>
        <Select
          labelId={labelId}
          id={selectId}
          label={label}
          value={safeValue}
          onChange={(event: SelectChangeEvent) => onSelectSensorPositionLimit(kind, event.target.value)}
          displayEmpty
          inputProps={{ 'aria-label': label }}
        >
          <MenuItem value={SENSOR_LIMIT_NONE}>{t('rotationalMechanism.sensor_limit_none', { defaultValue: 'None' })}</MenuItem>
          {supportedSensorOptions.map((option) => (
            <MenuItem key={option.variableName} value={option.variableName}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const usedPorts = useMemo(() => {
    const lead =
      typeof leadMotorPortControlValue === 'number' &&
      Number.isFinite(leadMotorPortControlValue) &&
      leadMotorPortControlValue >= 1 &&
      leadMotorPortControlValue <= 8
        ? [leadMotorPortControlValue]
        : [];
    return new Set<number>([...lead, ...followerMotors.filter(isSupportedFollowerMotor).map((motor) => motor.port)]);
  }, [leadMotorPortControlValue, followerMotors]);
  const availablePorts = useMemo(() => MOTOR_PORTS.filter((port) => !usedPorts.has(port)), [usedPorts]);
  const availableFollowerSlots = Math.min(MAX_FOLLOWER_MOTORS - followerMotors.length, availablePorts.length);

  const addFollowerMotor = () => {
    if (followerMotors.length >= MAX_FOLLOWER_MOTORS) return;
    const nextPort = availablePorts[0];
    if (!Number.isFinite(nextPort)) return;
    const nextFollowers = [
      ...followerMotors,
      {
        kind: 'supported' as const,
        port: nextPort,
        motorDirection: MOTOR_DIRECTION.forward,
        name: nextDefaultBoostMotorName(followerMotors),
      },
    ];
    applyFollowerMotorEdits(nextFollowers);
    setActiveMotorTab(nextFollowers.length);
  };

  const removeFollowerMotor = (followerIndex: number) => {
    const nextFollowers = followerMotors.filter((_, index) => index !== followerIndex);
    applyFollowerMotorEdits(nextFollowers);
  };

  const updateFollowerMotor = (followerIndex: number, nextValue: Partial<SupportedFollowerMotorConfig>) => {
    const nextFollowers = followerMotors.map((motor, index) =>
      index === followerIndex && isSupportedFollowerMotor(motor) ? { ...motor, ...nextValue } : motor
    );
    applyFollowerMotorEdits(nextFollowers);
  };

  const getUnsupportedFollowerReplacementMotor = (motor: UnsupportedFollowerMotorConfig, followerIndex: number): SupportedFollowerMotorConfig => {
    const rawPort = motor.rawCode.match(/\bport\s*=\s*([^,)]+)/)?.[1]?.trim();
    const parsedPort = rawPort && /^\d+$/.test(rawPort) ? Number.parseInt(rawPort, 10) : null;
    const parsedPortIsAvailable = parsedPort !== null && MOTOR_PORTS.includes(parsedPort) && !usedPorts.has(parsedPort);
    return {
      kind: 'supported',
      port: parsedPortIsAvailable ? parsedPort : availablePorts[0] ?? parsedPort ?? MOTOR_PORTS[0],
      motorDirection: MOTOR_DIRECTION.forward,
      name: motor.name && motor.name !== 'Custom Motor' ? motor.name : defaultBoostMotorName(followerIndex),
    };
  };

  const replaceUnsupportedFollowerMotor = (followerIndex: number, nextValue: Partial<SupportedFollowerMotorConfig>) => {
    const nextFollowers = followerMotors.map((motor, index) => {
      if (index !== followerIndex) return motor;
      if (isSupportedFollowerMotor(motor)) return { ...motor, ...nextValue };
      return { ...getUnsupportedFollowerReplacementMotor(motor, followerIndex), ...nextValue };
    });
    applyFollowerMotorEdits(nextFollowers);
  };

  const renderFollowerPanel = (followerIndex: number) => {
    const motor = followerMotors[followerIndex];
    if (!motor) return null;
    const followerId = `${props.id}:follower_motor_${followerIndex}`;
    if (motor.kind === 'unsupported') {
      const replacementMotor = getUnsupportedFollowerReplacementMotor(motor, followerIndex);
      return (
        <Stack direction="column" spacing={2}>
          <UnsupportedCodeStatus
            code={motor.rawCode}
            descriptionI18nKey="controlPanel.devices.unsupportedFollowerMotor.description"
            descriptionDefaultValue="Unsupported follower motor: {{code}}"
          />
          <Typography variant="caption" color="text.secondary">
            {t('rotationalMechanism.unsupported_follower_replacement_caption', { defaultValue: 'Choose standard replacement motor values' })}
          </Typography>
          <TextField
            label={t('rotationalMechanism.motor_name', { defaultValue: 'Motor Name' })}
            variant="standard"
            value={replacementMotor.name}
            onChange={(event) => replaceUnsupportedFollowerMotor(followerIndex, { name: event.target.value })}
          />
          <FollowerMotorPortControl
            id={followerId}
            labelId={`follower-motor-port-label-${followerIndex}`}
            labelPopoverId={`follower-motor-port-popover-${followerIndex}`}
            labelPopover={t('portControl.motor_port_info_tooltip')}
            selectedPort={replacementMotor.port}
            onChange={(portNumber) => replaceUnsupportedFollowerMotor(followerIndex, { port: portNumber })}
          />
          <Stack direction="column" spacing={2}>
            <InputLabel id={`follower-motor-direction-radio-label-${followerIndex}`}>
              <Stack direction="row" spacing={1}>
                <Typography>
                  <Trans i18nKey="motorCard.motor-direction-label">Motor Direction</Trans>
                </Typography>
                <HelpPopover id={`follower-motor-direction-popover-${followerIndex}`} text={t('motorCard.motor-direction-info-tooltip')} />
              </Stack>
            </InputLabel>
            <FormControl sx={{ marginTop: '0px !important' }}>
              <RadioGroup
                aria-labelledby={`follower-motor-direction-radio-label-${followerIndex}`}
                name={`follower-motor-direction-radio-group-${followerIndex}`}
                value={replacementMotor.motorDirection}
                onChange={(_event, value) =>
                  replaceUnsupportedFollowerMotor(followerIndex, {
                    motorDirection: value === MOTOR_DIRECTION.reverse ? MOTOR_DIRECTION.reverse : MOTOR_DIRECTION.forward,
                  })
                }
              >
                <FormControlLabel
                  value={MOTOR_DIRECTION.forward}
                  control={<Radio />}
                  label={
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Trans i18nKey="motorCard.motor-direction.forward">Forward</Trans>
                      {replacementMotor.motorDirection === MOTOR_DIRECTION.forward ? (
                        <ArrowUpwardIcon fontSize="medium" sx={{ mt: -0.25, flexShrink: 0 }} aria-hidden />
                      ) : null}
                    </Stack>
                  }
                />
                <FormControlLabel
                  value={MOTOR_DIRECTION.reverse}
                  control={<Radio />}
                  label={
                    <Stack direction="row" spacing={1} alignItems="flex-end">
                      <Trans i18nKey="motorCard.motor-direction.reverse">Reverse</Trans>
                      {replacementMotor.motorDirection === MOTOR_DIRECTION.reverse ? (
                        <ArrowDownwardIcon fontSize="medium" sx={{ mb: -0.25, flexShrink: 0 }} aria-hidden />
                      ) : null}
                    </Stack>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Stack>
        </Stack>
      );
    }

    return (
      <Stack direction="column" spacing={2}>
        <TextField
          label={t('rotationalMechanism.motor_name', { defaultValue: 'Motor Name' })}
          variant="standard"
          value={motor.name || defaultBoostMotorName(followerIndex)}
          onChange={(event) => updateFollowerMotor(followerIndex, { name: event.target.value })}
        />
        <FollowerMotorPortControl
          id={followerId}
          labelId={`follower-motor-port-label-${followerIndex}`}
          labelPopoverId={`follower-motor-port-popover-${followerIndex}`}
          labelPopover={t('portControl.motor_port_info_tooltip')}
          selectedPort={motor.port}
          onChange={(portNumber) => updateFollowerMotor(followerIndex, { port: portNumber })}
        />
        <Stack direction="column" spacing={2}>
          <InputLabel id={`follower-motor-direction-radio-label-${followerIndex}`}>
            <Stack direction="row" spacing={1}>
              <Typography>
                <Trans i18nKey="motorCard.motor-direction-label">Motor Direction</Trans>
              </Typography>
              <HelpPopover id={`follower-motor-direction-popover-${followerIndex}`} text={t('motorCard.motor-direction-info-tooltip')} />
            </Stack>
          </InputLabel>
          <FormControl sx={{ marginTop: '0px !important' }}>
            <RadioGroup
              aria-labelledby={`follower-motor-direction-radio-label-${followerIndex}`}
              name={`follower-motor-direction-radio-group-${followerIndex}`}
              value={motor.motorDirection}
              onChange={(_event, value) =>
                updateFollowerMotor(followerIndex, {
                  motorDirection: value === MOTOR_DIRECTION.reverse ? MOTOR_DIRECTION.reverse : MOTOR_DIRECTION.forward,
                })
              }
            >
              <FormControlLabel
                value={MOTOR_DIRECTION.forward}
                control={<Radio />}
                label={
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Trans i18nKey="motorCard.motor-direction.forward">Forward</Trans>
                    {motor.motorDirection === MOTOR_DIRECTION.forward ? (
                      <ArrowUpwardIcon fontSize="medium" sx={{ mt: -0.25, flexShrink: 0 }} aria-hidden />
                    ) : null}
                  </Stack>
                }
              />
              <FormControlLabel
                value={MOTOR_DIRECTION.reverse}
                control={<Radio />}
                label={
                  <Stack direction="row" spacing={1} alignItems="flex-end">
                    <Trans i18nKey="motorCard.motor-direction.reverse">Reverse</Trans>
                    {motor.motorDirection === MOTOR_DIRECTION.reverse ? (
                      <ArrowDownwardIcon fontSize="medium" sx={{ mb: -0.25, flexShrink: 0 }} aria-hidden />
                    ) : null}
                  </Stack>
                }
              />
            </RadioGroup>
          </FormControl>
        </Stack>
      </Stack>
    );
  };

  const motorPanelCount = followerMotors.length + 1;
  const getMotorPanelLabel = (panelIndex: number) =>
    panelIndex === 0
      ? t('rotationalMechanism.lead_motor', { defaultValue: 'Lead Motor' })
      : (followerMotors[panelIndex - 1]?.name || '').trim() ||
        t('rotationalMechanism.follower_motor_number', { defaultValue: `Boost ${panelIndex}`, number: panelIndex });

  const motorPanelNavigation =
    followerMotors.length > 0 ? (
      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
        <IconButton
          size="small"
          onClick={() => setActiveMotorTab((prev) => Math.max(prev - 1, 0))}
          disabled={activeMotorTab <= 0}
          aria-label={t('rotationalMechanism.previous_motor', { defaultValue: 'Previous motor panel' })}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="center"
          role="navigation"
          aria-label={t('rotationalMechanism.motor_panel_pages', { defaultValue: 'Motor panel pages' })}
          sx={{ minWidth: 0 }}
        >
          {Array.from({ length: motorPanelCount }, (_, panelIndex) => {
            const selected = panelIndex === activeMotorTab;
            const panelLabel = getMotorPanelLabel(panelIndex);
            return (
              <IconButton
                key={panelIndex}
                size="small"
                onClick={() => setActiveMotorTab(panelIndex)}
                aria-label={t('rotationalMechanism.select_motor_panel', { defaultValue: `Show ${panelLabel}`, label: panelLabel })}
                aria-current={selected ? 'page' : undefined}
                sx={{ width: 24, height: 24, p: 0 }}
              >
                <Box
                  component="span"
                  sx={{
                    width: selected ? 8 : 6,
                    height: selected ? 8 : 6,
                    borderRadius: '50%',
                    bgcolor: selected ? 'primary.main' : 'action.disabled',
                  }}
                />
              </IconButton>
            );
          })}
        </Stack>
        <IconButton
          size="small"
          onClick={() => setActiveMotorTab((prev) => Math.min(prev + 1, followerMotors.length))}
          disabled={activeMotorTab >= followerMotors.length}
          aria-label={t('rotationalMechanism.next_motor', { defaultValue: 'Next motor panel' })}
        >
          <ChevronRightIcon />
        </IconButton>
      </Stack>
    ) : null;

  const motorPanelActions = (
    <Stack direction="row" alignItems="center" justifyContent={activeMotorTab > 0 ? 'space-between' : 'flex-end'} sx={{ mt: 1 }}>
      {activeMotorTab > 0 && (
        <Button
          variant="text"
          color="error"
          size="small"
          startIcon={<DeleteIcon fontSize="small" />}
          onClick={() => removeFollowerMotor(activeMotorTab - 1)}
          sx={{ minWidth: 0, px: 0.5 }}
        >
          <Trans i18nKey="rotationalMechanism.remove_follower_motor">Remove Motor</Trans>
        </Button>
      )}
      <Stack direction="row" spacing={0.25} alignItems="center">
        <Button
          variant="text"
          size="small"
          startIcon={<AddIcon fontSize="small" />}
          onClick={addFollowerMotor}
          disabled={availableFollowerSlots <= 0}
          sx={{ minHeight: MOTOR_BUTTON_HEIGHT, height: MOTOR_BUTTON_HEIGHT, minWidth: 0, px: 0.5, textTransform: 'none' }}
        >
          <Trans i18nKey="rotationalMechanism.add_follower_motor">Motor</Trans>
        </Button>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: MOTOR_BUTTON_HEIGHT,
            '& > div': { display: 'flex', alignItems: 'center' },
            '& svg': { display: 'block' },
          }}
        >
          <HelpPopover
            id="add-follower-motor-popover"
            text={t('rotationalMechanism.add_follower_motor_info_tooltip', {
              defaultValue: 'Add a boost motor to assist the lead motor. Boost motors use the same motor type and only expose port and direction.',
            })}
          />
        </Box>
      </Stack>
    </Stack>
  );

  return (
    <DeviceCardWrapper header={props.header} headerIcon={HeaderIcon} initialOpen={props.initialOpen} onDelete={props.onDelete}>
      <Accordion expanded={motorsExpanded} onChange={(_event, expanded) => setMotorsExpanded(expanded)} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="motors-accordion-content" id="motors-accordion-header">
          <Typography>
            <Trans i18nKey="rotationalMechanism.motors">Motors</Trans>
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction="column" spacing={2}>
            <MotorTypeControl {...props} parameterName="motor_type" />
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                padding: 2,
                backgroundColor: 'background.default',
              }}
            >
              <Stack direction="column" spacing={2}>
                {activeMotorTab === 0 ? (
                  <Stack direction="column" spacing={2}>
                    <TextField
                      label={t('rotationalMechanism.motor_name', { defaultValue: 'Motor Name' })}
                      variant="standard"
                      value={t('rotationalMechanism.lead_motor', { defaultValue: 'Lead Motor' })}
                      disabled
                    />
                    <DriveUnitPortControl
                      {...props}
                      parameterName="motor_port"
                      portControlValue={leadMotorPortControlValue}
                      setPortControlValue={setLeadMotorPortControlValue}
                      unsupportedRawValue={leadMotorUnsupportedRawValue}
                      label={<Trans i18nKey="inputControl.motor_port">Motor Port</Trans>}
                      labelId="motor-port-label"
                      labelPopoverId="motor-port-popover"
                      labelPopover={t('portControl.motor_port_info_tooltip')}
                      selectId="motor-port"
                      selectLabelId="select-motor-port-label"
                    />
                    <MotorDirectionControl {...props} parameterName="motor_direction" />
                    <SliderControl
                      {...props}
                      parameterName="max_torque"
                      label={<Trans i18nKey="motorControl.gear_ratio">Max Torque</Trans>}
                      labelId="max-torque"
                      labelPopoverId="max-torque-popover"
                      labelPopover={t('motorControl.max_torque_info_tooltip')}
                      labelUnits="N·m"
                      step={0.01}
                      min={torqueLimits.MIN_TORQUE}
                      max={torqueLimits.MAX_TORQUE}
                      shrinkLabel={false}
                    />
                    <DropDownControl
                      {...props}
                      parameterName="stop_mode"
                      label={<Trans i18nKey="motorControl.max_position_limit">Stopping Mode</Trans>}
                      labelId="stopping-mode"
                      labelPopoverId="stopping-mode-popover"
                      labelPopover={t('motorControl.stopping_mode_info_tooltip')}
                      selectId="stopping-mode"
                      selectLabelId="select-stopping-mode-label"
                      options={[
                        { label: 'Brake', value: 'MotorStopMode.BRAKE' },
                        { label: 'Coast', value: 'MotorStopMode.COAST' },
                        { label: 'Hold', value: 'MotorStopMode.HOLD' },
                      ]}
                    />
                  </Stack>
                ) : (
                  renderFollowerPanel(activeMotorTab - 1)
                )}
                {motorPanelActions}
              </Stack>
            </Box>
            {motorPanelNavigation}
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Accordion expanded={mechanismExpanded} onChange={(_event, expanded) => setMechanismExpanded(expanded)} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="mechanism-accordion-content" id="mechanism-accordion-header">
          <Typography>
            <Trans i18nKey="rotationalMechanism.mechanism">Mechanism</Trans>
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction="column" spacing={2}>
            <Stack direction="column" spacing={1}>
              <InputLabel id="gear-ratio-label" sx={{ paddingBottom: 1 }}>
                <Stack direction="row" spacing={1}>
                  <Typography>
                    <Trans i18nKey="inputControl.gear_ratio">Gear Ratio</Trans>
                  </Typography>
                  <HelpPopover id="gear-ratio-popover" text={t('inputControl.gear_ratio_info_tooltip')} />
                </Stack>
              </InputLabel>
              <UnsupportedCodeStatus
                code={unsupportedGearRatioRawValue}
                descriptionI18nKey="controlPanel.devices.unsupportedGearRatioParameter.description"
                descriptionDefaultValue="Unsupported gear ratio: {{code}}"
              />
              {!isGearRatioComputedInRange && isGearRatioInputValid && isGearRatioOutputValid && (
                <Typography variant="caption" color="error.main">
                  {t('mechanism.gear_ratio_ratio_error', {
                    defaultValue: `The computed gear ratio must be between ${GEAR_RATIO_COMPUTED_MIN} and ${GEAR_RATIO_FIELD_MAX}.`,
                  })}
                </Typography>
              )}
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <TextField
                  label={t('mechanism.gear_ratio_input', { defaultValue: 'Input' })}
                  variant="standard"
                  type="number"
                  value={gearRatioInput}
                  sx={{ maxWidth: 80 }}
                  onFocus={() => {
                    activeGearRatioEditRef.current = 'input';
                  }}
                  onChange={(event) => setGearRatioInput(event.target.value)}
                  onBlur={() => {
                    activeGearRatioEditRef.current = null;
                    commitGearRatio();
                  }}
                  error={gearRatioInput.length > 0 && !isGearRatioInputValid}
                  helperText={
                    gearRatioInput.length > 0 && !isGearRatioInputValid
                      ? t('mechanism.gear_ratio_input_error', {
                          defaultValue: `Enter a number between ${GEAR_RATIO_FIELD_MIN} and ${GEAR_RATIO_FIELD_MAX}.`,
                        })
                      : undefined
                  }
                  slotProps={{
                    input: {
                      inputProps: { min: GEAR_RATIO_FIELD_MIN, max: GEAR_RATIO_FIELD_MAX, step: 0.01 },
                    },
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    // Align with the actual input row (not the label / helper text)
                    pt: 2.5,
                    minWidth: 0,
                  }}
                >
                  <Typography variant="body1" color="text.primary" fontWeight="bold">
                    :
                  </Typography>
                </Box>
                <TextField
                  label={t('mechanism.gear_ratio_output', { defaultValue: 'Output' })}
                  variant="standard"
                  type="number"
                  value={gearRatioOutput}
                  sx={{ maxWidth: 80 }}
                  onFocus={() => {
                    activeGearRatioEditRef.current = 'output';
                  }}
                  onChange={(event) => setGearRatioOutput(event.target.value)}
                  onBlur={() => {
                    activeGearRatioEditRef.current = null;
                    commitGearRatio();
                  }}
                  error={gearRatioOutput.length > 0 && !isGearRatioOutputValid}
                  helperText={
                    gearRatioOutput.length > 0 && !isGearRatioOutputValid
                      ? t('mechanism.gear_ratio_output_error', {
                          defaultValue: `Enter a number between ${GEAR_RATIO_FIELD_MIN} and ${GEAR_RATIO_FIELD_MAX}.`,
                        })
                      : undefined
                  }
                  slotProps={{
                    input: {
                      inputProps: { min: GEAR_RATIO_FIELD_MIN, max: GEAR_RATIO_FIELD_MAX, step: 0.01 },
                    },
                  }}
                />
                {gearRatioDecimalPreview && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      pt: 2.5,
                      minWidth: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Typography variant="body1" color="text.primary" fontWeight="bold">
                      = {gearRatioDecimalPreview}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Stack>
            {isLinearMechanism && (
              <InputControl
                {...props}
                parameterName="distance_per_output_rotation"
                label={<Trans i18nKey="inputControl.distance_per_output_rotation">Distance Per Output Rotation</Trans>}
                labelId="distance-per-output-rotation"
                labelPopoverId="distance-per-output-rotation-popover"
                labelPopover={t('inputControl.distance_per_output_rotation_info_tooltip')}
                step={0.1}
                min={0}
                unitLabel="m"
              />
            )}
            <Stack direction="column" spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <FormControlLabel
                  control={<Switch checked={isSoftwarePositionLimitsEnabled === true} onChange={onToggleSoftwarePositionLimits} />}
                  label={<Trans i18nKey="rotationalMechanism.enable_software_position_limits">Enable software position limits</Trans>}
                />
                <HelpPopover id="position-limits-popover" text={t('rotationalMechanism.position_limits_info_tooltip')} />
              </Stack>
              <UnsupportedCodeStatus
                code={unsupportedSoftwareLimitsRawValue}
                descriptionI18nKey="controlPanel.devices.unsupportedBooleanParameter.description"
                descriptionDefaultValue="Unsupported setting value: {{code}}"
              />

              {isSoftwarePositionLimitsEnabled === true && softwarePositionLimitControls}
            </Stack>
            <Stack direction="column" spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <FormControlLabel
                  control={<Switch checked={isSensorPositionLimitsEnabled === true} onChange={onToggleSensorPositionLimits} />}
                  label={<Trans i18nKey="rotationalMechanism.enable_sensor_position_limits">Enable sensor position limits</Trans>}
                />
                <HelpPopover
                  id="sensor-position-limits-popover"
                  text={t('rotationalMechanism.sensor_position_limits_info_tooltip', {
                    defaultValue: 'Use supported sensors to stop motion at the minimum or maximum position limit.',
                  })}
                />
              </Stack>
              <UnsupportedCodeStatus
                code={unsupportedSensorLimitsRawValue}
                descriptionI18nKey="controlPanel.devices.unsupportedBooleanParameter.description"
                descriptionDefaultValue="Unsupported setting value: {{code}}"
              />

              {isSensorPositionLimitsEnabled === true &&
                (supportedSensorOptions.length > 0 ? (
                  <Stack direction="column" spacing={2}>
                    {renderSensorPositionLimitSelect('min')}
                    {renderSensorPositionLimitSelect('max')}
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {t('rotationalMechanism.no_supported_sensor_limits', { defaultValue: 'No supported sensors are in this program.' })}
                  </Typography>
                ))}
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </DeviceCardWrapper>
  );
};

export default RotationalMechanism;
